import React, { useEffect, useRef, useState } from 'react';
import { ActionHeader, BaseActionType } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import { IActionLinkItems } from 'components/ActionLinks/ActionLinkModels';
import { Placement } from 'popper.js';
import { ActionLinksCallout } from './ActionLinksCallout';
import { fetchActions, getActionLinksForActions, IActionHandler } from 'utils/ActionUtils';

export type IActionsCalloutProps = {
  sourceRef: React.MutableRefObject<HTMLElement | null>;
  placement?: Placement;
  nestedPlacement?: Placement;
  onAction?: IActionHandler;
  onClose: () => void;
} & (
  | {
      entityId: string;
      entityType: string;
      preLoadedActions?: never;
      actionLinks?: never;
    }
  | {
      entityId?: never;
      entityType?: never;
      preLoadedActions: BaseActionType[] | ActionHeader[];
      actionLinks?: never;
    }
);

/**
 * Render, or fetch and then render, Decisions Actions.
 *
 * If you already have ActionLinks processed, use `ActionLinksCallout`, which this uses internally.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function ActionsCallout({
  entityId,
  entityType,
  preLoadedActions,
  actionLinks,
  onAction,
  ...calloutProps
}: IActionsCalloutProps) {
  const [actionsById, setActionsById] = useState<Map<string, BaseActionType | ActionHeader>>(new Map());
  const [links, setActions] = useState<IActionLinkItems>();
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(new AbortController());
  const actionHandler = onAction || calloutProps.onClose;

  const getActions = async () => {
    const abort = abortRef.current;
    let acts = preLoadedActions ? getActionLinksForActions(preLoadedActions, actionHandler) : undefined;
    if (!acts) {
      if (!entityId || !entityType) {
        return console.warn('Bad ActionsCallout Config');
      }
      // fetch if we need to:
      setLoading(true);
      const fetchedActs = await fetchActions(entityId, entityType, abortRef.current);
      if (!abort.signal.aborted) {
        acts = getActionLinksForActions(fetchedActs, actionHandler);
        setActions(acts.actionLinks);
        setActionsById(acts.originalActionsById);
        setLoading(false);
      }
    } else {
      setActions(acts.actionLinks);
      setActionsById(acts.originalActionsById);
    }
  };

  useEffect(() => {
    const abort = abortRef.current;
    // this should only run once when this mounts, and once when it unmounts.
    // so we don't need overhead of useCallback here.
    getActions();
    return () => abort.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <ActionLinksCallout links={links} actionsById={actionsById} loading={loading} {...calloutProps} />;
}
